<script setup>
/* Guscio comune a tutte le modali: sfondo, chiusura con Esc, clic fuori,
 * focus trap e ripristino del focus all'uscita.
 *
 * Nell'originale niente di tutto questo esisteva: le modali di Buefy si
 * chiudevano con `$parent.close()` e non gestivano né il focus né la
 * tastiera. */
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
  title: { type: String, default: '' },
  /** Larghezza massima del pannello. */
  size: { type: String, default: 'normal' }, // normal | wide | full
});

const emit = defineEmits(['close']);

const panel = ref(null);
let previouslyFocused = null;

function onKeydown(event) {
  if (event.key === 'Escape') {
    event.stopPropagation();
    emit('close');
    return;
  }
  if (event.key !== 'Tab' || !panel.value) return;

  const focusable = panel.value.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

onMounted(async () => {
  previouslyFocused = document.activeElement;
  document.body.style.overflow = 'hidden';
  await nextTick();
  // Il fallback sul pannello non è cosmetico: senza un elemento focalizzato
  // dentro la modale, il keydown non risalirebbe mai fino a `.backdrop` e la
  // chiusura con Esc non funzionerebbe.
  const target = panel.value?.querySelector('[autofocus], input, textarea, button');
  (target ?? panel.value)?.focus();
});

onBeforeUnmount(() => {
  document.body.style.overflow = '';
  previouslyFocused?.focus?.();
});
</script>

<template>
  <div class="backdrop" :class="`backdrop--${props.size}`" @keydown="onKeydown">
    <!-- mousedown e non click: altrimenti un trascinamento iniziato dentro il
         pannello e concluso fuori chiuderebbe la modale. -->
    <div class="backdrop__hit" @mousedown.self="emit('close')" />
    <div
      ref="panel"
      class="panel"
      :class="`panel--${props.size}`"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      :aria-label="props.title || undefined"
    >
      <header v-if="props.title || $slots.header" class="panel__head">
        <slot name="header">
          <h2 class="panel__title display">{{ props.title }}</h2>
        </slot>
        <button class="panel__close" type="button" :aria-label="$t('closeLabel')" @click="emit('close')">
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.6" fill="none" />
          </svg>
        </button>
      </header>
      <div class="panel__body"><slot /></div>
      <footer v-if="$slots.footer" class="panel__foot"><slot name="footer" /></footer>
    </div>
  </div>
</template>

<style scoped>
.backdrop {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: grid;
  place-items: center;
  padding: var(--s-4);
  animation: fade var(--dur) var(--ease);
}

.backdrop__hit {
  position: absolute;
  inset: 0;
  background: rgb(12 13 16 / 62%);
  backdrop-filter: blur(2px);
}

/* Il lightbox è l'unica modale che deve far *sparire* l'app, non velarla: al
   62% la griglia dietro resta leggibile e l'occhio continua a cercarla mentre
   dovrebbe guardare la fotografia. Le altre modali sono pannelli di lavoro,
   e lì vedere dove si è aiuta. */
.backdrop--full .backdrop__hit {
  background: rgb(9 10 12 / 94%);
  backdrop-filter: blur(6px);
}

.panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 30rem;
  max-height: min(90vh, 54rem);
  background: var(--surface-raised);
  border: 1px solid var(--rule);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-overlay);
}

.panel--wide {
  max-width: 52rem;
}

/* Il pannello prende il focus solo perché Esc e il focus trap funzionino (vedi
   onMounted): non è un comando, e l'anello del focus disegnava una cornice
   azzurra intorno a tutto il lightbox appena si toccava un tasto. I controlli
   veri dentro il pannello continuano ad averlo. */
.panel:focus-visible {
  outline: none;
}

.panel--full {
  max-width: min(96vw, 90rem);
  max-height: 94vh;
  background: transparent;
  border: none;
  box-shadow: none;
}

.panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-4);
  padding: var(--s-4) var(--s-5);
  border-bottom: 1px solid var(--rule);
}

.panel__title {
  font-size: var(--t-md);
}

.panel__close {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid transparent;
  border-radius: var(--radius);
  background: transparent;
  color: var(--ink-muted);
  cursor: pointer;
}

.panel__close:hover {
  background: var(--bg);
  border-color: var(--rule);
  color: var(--ink);
}

.panel__body {
  overflow-y: auto;
  padding: var(--s-5);
}

.panel--full .panel__body {
  padding: 0;
  overflow: visible;
}

.panel__foot {
  display: flex;
  justify-content: flex-end;
  gap: var(--s-3);
  padding: var(--s-4) var(--s-5);
  border-top: 1px solid var(--rule);
}

@keyframes fade {
  from {
    opacity: 0;
  }
}
</style>
